/**
 * Structural + semantic validation of print templates and sheets.
 *
 * `parsePrintTemplateSpec` / `parseSheetDoc` take UNTRUSTED JSON (GraphQL
 * JSON scalar, imported files) and return either a fully typed value (rebuilt
 * field by field — unknown keys are dropped) or the list of issues. The same
 * functions run on the server (save/publish) and in the editors (inline
 * messages).
 */

import { GRADIENT_TYPES } from '../gradients';
import type { PrintColor } from './color';
import { CUSTOM_PAPER_LIMITS, PAPER_IDS, type PaperOrientation, type PaperRef } from './paper';
import {
  BUNDLED_PRINT_FONTS,
  PRINT_FIELD_KEY_PATTERN,
  PRINT_TEMPLATE_LIMITS,
  PRINT_TEMPLATE_SCHEMA_VERSION,
  VIEWPORT_KINDS,
  forEachElement,
  type FontRef,
  type FontWeight,
  type HorizontalAlign,
  type ImageFit,
  type ImageSource,
  type LegendItem,
  type LegendSource,
  type LineCap,
  type LineJoin,
  type NamedTextStyle,
  type NorthArrowVariant,
  type PageMargins,
  type PageRepeat,
  type PrintFieldDef,
  type PrintFieldType,
  type PrintPageSpec,
  type PrintTemplateElement,
  type PrintTemplateSpec,
  type StrokeStyle,
  type TableColumn,
  type TableRows,
  type TextStyle,
  type TextStyleRef,
  type VerticalAlign,
  type ViewportKind,
} from './spec';
import {
  PLAN_LAYER_CATEGORIES,
  PLAN_TEXT_CATEGORIES,
  SHEET_DOC_SCHEMA_VERSION,
  SHEET_DIMENSION_ENDS,
  SHEET_DIMENSION_EXTENSION_STYLES,
  SHEET_DIMENSION_LINE_STYLES,
  SHEET_EDITABLE_KINDS,
  type HeatmapViewportSettings,
  type PlanLayerCategory,
  type PlanLayerPen,
  type PlanTextCategory,
  type PrintTemplateRef,
  type SheetDoc,
  type SheetDimension,
  type SheetElementEdit,
  type SheetRevision,
  type ViewportSettings,
  type XrayViewportSettings,
} from './sheet';
import { textSizeToEmMm, type TextSize, type TextSizeBasis, type TextSizeUnit } from './units';

export interface PrintTemplateIssue {
  /** Dotted path into the value, e.g. `pages[0].elements[3].style.size`. */
  path: string;
  message: string;
}

export type PrintTemplateParseResult = { ok: true; spec: PrintTemplateSpec } | { ok: false; issues: PrintTemplateIssue[] };
export type SheetDocParseResult = { ok: true; doc: SheetDoc } | { ok: false; issues: PrintTemplateIssue[] };

type UnknownRecord = Record<string, unknown>;

const ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
/** Floor ids come from the project (uuids / Mongo ids) — anything without key separators. */
const FLOOR_ID_PATTERN = /^[^:/\s]{1,128}$/;
const VIEWPORT_KEY_PATTERN = /^[A-Za-z0-9_-]{1,64}(:[^:/\s]{1,128})?\/[A-Za-z0-9_-]{1,64}$/;

const FONT_WEIGHTS: readonly FontWeight[] = ['normal', 'bold'];
const TEXT_SIZE_UNITS: readonly TextSizeUnit[] = ['pt', 'mm'];
const TEXT_SIZE_BASES: readonly TextSizeBasis[] = ['em', 'cap'];
const LINE_CAPS: readonly LineCap[] = ['butt', 'round', 'square'];
const LINE_JOINS: readonly LineJoin[] = ['miter', 'round', 'bevel'];
const H_ALIGNS: readonly HorizontalAlign[] = ['left', 'center', 'right'];
const V_ALIGNS: readonly VerticalAlign[] = ['top', 'middle', 'bottom'];
const IMAGE_FITS: readonly ImageFit[] = ['contain', 'cover', 'stretch'];
const NORTH_ARROW_VARIANTS: readonly NorthArrowVariant[] = ['arrow', 'triangle', 'compass'];
const LEGEND_SOURCES: readonly LegendSource[] = ['heatmap', 'layers', 'manual'];
const ORIENTATIONS: readonly PaperOrientation[] = ['portrait', 'landscape'];
const PAGE_REPEATS: readonly PageRepeat[] = ['none', 'perFloor'];
const FIELD_TYPES: readonly PrintFieldType[] = ['text', 'multiline', 'date', 'select'];
const PAGE_MAX_MM = CUSTOM_PAPER_LIMITS.maxMm * 2;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[]): value is T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value);
}

/** Collects issues while rebuilding typed values; every reader returns a safe fallback on failure. */
class Reader {
  readonly issues: PrintTemplateIssue[] = [];

  fail(path: string, message: string): void {
    this.issues.push({ path, message });
  }

  record(value: unknown, path: string): UnknownRecord {
    if (isRecord(value)) return value;
    this.fail(path, 'must be an object');
    return {};
  }

  array(value: unknown, path: string, maxLength: number): unknown[] {
    if (!Array.isArray(value)) {
      this.fail(path, 'must be an array');
      return [];
    }
    if (value.length > maxLength) this.fail(path, `must have at most ${maxLength} entries`);
    return value.slice(0, maxLength);
  }

  number(value: unknown, path: string, opts: { min?: number; max?: number; exclusiveMin?: number } = {}): number {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      this.fail(path, 'must be a finite number');
      return opts.min ?? (opts.exclusiveMin !== undefined ? opts.exclusiveMin + 1 : 0);
    }
    if (opts.min !== undefined && value < opts.min) this.fail(path, `must be ≥ ${opts.min}`);
    if (opts.exclusiveMin !== undefined && value <= opts.exclusiveMin) this.fail(path, `must be > ${opts.exclusiveMin}`);
    if (opts.max !== undefined && value > opts.max) this.fail(path, `must be ≤ ${opts.max}`);
    return value;
  }

  integer(value: unknown, path: string, opts: { min?: number; max?: number } = {}): number {
    const n = this.number(value, path, opts);
    if (!Number.isInteger(n)) this.fail(path, 'must be a whole number');
    return Math.round(n);
  }

  nullableNumber(value: unknown, path: string): number | null {
    return value === null || value === undefined ? null : this.number(value, path);
  }

  string(value: unknown, path: string, maxLength: number = PRINT_TEMPLATE_LIMITS.maxLabelLength): string {
    if (typeof value !== 'string') {
      this.fail(path, 'must be a string');
      return '';
    }
    if (value.length > maxLength) this.fail(path, `must be at most ${maxLength} characters`);
    return value;
  }

  nullableString(value: unknown, path: string, maxLength?: number): string | null {
    return value === undefined || value === null ? null : this.string(value, path, maxLength);
  }

  id(value: unknown, path: string): string {
    const id = this.string(value, path, 64);
    if (!ID_PATTERN.test(id)) this.fail(path, 'must be 1–64 letters, digits, "_" or "-"');
    return id;
  }

  boolean(value: unknown, path: string): boolean {
    if (typeof value !== 'boolean') {
      this.fail(path, 'must be a boolean');
      return false;
    }
    return value;
  }

  optionalBoolean(value: unknown, path: string): boolean | undefined {
    return value === undefined || value === null ? undefined : this.boolean(value, path);
  }

  enumValue<T extends string>(value: unknown, allowed: readonly T[], path: string, fallback: T): T {
    if (!oneOf(value, allowed)) {
      this.fail(path, `must be one of ${allowed.join(', ')}`);
      return fallback;
    }
    return value;
  }
}

// ─── Shared value readers ───────────────────────────────────────────────

function readUnit(r: Reader, value: unknown, path: string): number {
  return r.number(value, path, { min: 0, max: 1 });
}

function readColor(r: Reader, value: unknown, path: string): PrintColor {
  const v = r.record(value, path);
  switch (v['space']) {
    case 'gray':
      return { space: 'gray', k: readUnit(r, v['k'], `${path}.k`) };
    case 'cmyk':
      return { space: 'cmyk', c: readUnit(r, v['c'], `${path}.c`), m: readUnit(r, v['m'], `${path}.m`), y: readUnit(r, v['y'], `${path}.y`), k: readUnit(r, v['k'], `${path}.k`) };
    case 'rgb':
      return { space: 'rgb', r: readUnit(r, v['r'], `${path}.r`), g: readUnit(r, v['g'], `${path}.g`), b: readUnit(r, v['b'], `${path}.b`) };
    default:
      r.fail(`${path}.space`, 'must be gray, cmyk or rgb');
      return { space: 'gray', k: 1 };
  }
}

function readNullableColor(r: Reader, value: unknown, path: string): PrintColor | null {
  return value === null || value === undefined ? null : readColor(r, value, path);
}

function readTextSize(r: Reader, value: unknown, path: string): TextSize {
  const v = r.record(value, path);
  const size: TextSize = {
    value: r.number(v['value'], `${path}.value`, { exclusiveMin: 0 }),
    unit: r.enumValue(v['unit'], TEXT_SIZE_UNITS, `${path}.unit`, 'pt'),
    basis: r.enumValue(v['basis'], TEXT_SIZE_BASES, `${path}.basis`, 'em'),
  };
  const emMm = textSizeToEmMm(size);
  if (emMm < PRINT_TEMPLATE_LIMITS.minTextEmMm || emMm > PRINT_TEMPLATE_LIMITS.maxTextEmMm) {
    r.fail(path, `must be between ${PRINT_TEMPLATE_LIMITS.minTextEmMm} and ${PRINT_TEMPLATE_LIMITS.maxTextEmMm} mm (em)`);
  }
  return size;
}

function readFontRef(r: Reader, value: unknown, path: string): FontRef {
  const v = r.record(value, path);
  if (v['source'] === 'org') return { source: 'org', assetId: r.string(v['assetId'], `${path}.assetId`, 128) };
  if (v['source'] !== 'bundled') r.fail(`${path}.source`, 'must be bundled or org');
  return { source: 'bundled', family: r.enumValue(v['family'], BUNDLED_PRINT_FONTS, `${path}.family`, 'Inter') };
}

/** Reads the TextStyle keys present on `v`; `required` demands all of them. */
function readTextStyleFields(r: Reader, v: UnknownRecord, path: string, required: boolean): Partial<TextStyle> {
  const out: Partial<TextStyle> = {};
  const has = (key: keyof TextStyle) => {
    if (v[key] !== undefined && v[key] !== null) return true;
    if (required) r.fail(`${path}.${key}`, 'is required');
    return false;
  };
  if (has('font')) out.font = readFontRef(r, v['font'], `${path}.font`);
  if (has('size')) out.size = readTextSize(r, v['size'], `${path}.size`);
  if (has('weight')) out.weight = r.enumValue(v['weight'], FONT_WEIGHTS, `${path}.weight`, 'normal');
  if (has('italic')) out.italic = r.boolean(v['italic'], `${path}.italic`);
  if (has('color')) out.color = readColor(r, v['color'], `${path}.color`);
  if (has('lineHeight')) out.lineHeight = r.number(v['lineHeight'], `${path}.lineHeight`, { min: 0.5, max: 5 });
  if (has('letterSpacingMm')) out.letterSpacingMm = r.number(v['letterSpacingMm'], `${path}.letterSpacingMm`, { min: -5, max: 50 });
  return out;
}

function readTextStyle(r: Reader, value: unknown, path: string): TextStyle {
  const partial = readTextStyleFields(r, r.record(value, path), path, true);
  return {
    font: partial.font ?? { source: 'bundled', family: 'Inter' },
    size: partial.size ?? { value: 10, unit: 'pt', basis: 'em' },
    weight: partial.weight ?? 'normal',
    italic: partial.italic ?? false,
    color: partial.color ?? { space: 'gray', k: 1 },
    lineHeight: partial.lineHeight ?? 1.2,
    letterSpacingMm: partial.letterSpacingMm ?? 0,
  };
}

function readTextStyleRef(r: Reader, value: unknown, path: string): TextStyleRef {
  if (value === undefined || value === null) return {};
  const v = r.record(value, path);
  const ref: TextStyleRef = readTextStyleFields(r, v, path, false);
  if (v['styleId'] !== undefined && v['styleId'] !== null) ref.styleId = r.id(v['styleId'], `${path}.styleId`);
  return ref;
}

function readStroke(r: Reader, value: unknown, path: string): StrokeStyle {
  const v = r.record(value, path);
  const dash = r.array(v['dashMm'] ?? [], `${path}.dashMm`, 8).map((d, i) => r.number(d, `${path}.dashMm[${i}]`, { exclusiveMin: 0, max: 100 }));
  return {
    color: readColor(r, v['color'], `${path}.color`),
    weightMm: r.number(v['weightMm'], `${path}.weightMm`, { min: 0, max: PRINT_TEMPLATE_LIMITS.maxLineWeightMm }),
    dashMm: dash,
    cap: r.enumValue(v['cap'] ?? 'butt', LINE_CAPS, `${path}.cap`, 'butt'),
    join: r.enumValue(v['join'] ?? 'miter', LINE_JOINS, `${path}.join`, 'miter'),
  };
}

function readNullableStroke(r: Reader, value: unknown, path: string): StrokeStyle | null {
  return value === null || value === undefined ? null : readStroke(r, value, path);
}

function readPaper(r: Reader, value: unknown, path: string): PaperRef {
  const v = r.record(value, path);
  if (v['kind'] === 'custom') {
    const limits = { min: CUSTOM_PAPER_LIMITS.minMm, max: CUSTOM_PAPER_LIMITS.maxMm };
    return { kind: 'custom', widthMm: r.number(v['widthMm'], `${path}.widthMm`, limits), heightMm: r.number(v['heightMm'], `${path}.heightMm`, limits) };
  }
  if (v['kind'] !== 'standard') r.fail(`${path}.kind`, 'must be standard or custom');
  return { kind: 'standard', id: r.enumValue(v['id'], PAPER_IDS, `${path}.id`, 'A4') };
}

function readMargins(r: Reader, value: unknown, path: string): PageMargins {
  const v = r.record(value, path);
  const side = (key: keyof PageMargins) => r.number(v[key], `${path}.${key}`, { min: 0, max: 200 });
  return { top: side('top'), right: side('right'), bottom: side('bottom'), left: side('left') };
}

// ─── Elements ───────────────────────────────────────────────────────────

function readElement(r: Reader, value: unknown, path: string, depth: number): PrintTemplateElement | null {
  const v = r.record(value, path);
  const base = {
    id: r.id(v['id'], `${path}.id`),
    ...(v['name'] !== undefined && v['name'] !== null ? { name: r.string(v['name'], `${path}.name`) } : {}),
    ...(v['locked'] !== undefined && v['locked'] !== null ? { locked: r.boolean(v['locked'], `${path}.locked`) } : {}),
    ...(v['hidden'] !== undefined && v['hidden'] !== null ? { hidden: r.boolean(v['hidden'], `${path}.hidden`) } : {}),
    xMm: r.number(v['xMm'], `${path}.xMm`, { min: -PAGE_MAX_MM, max: PAGE_MAX_MM }),
    yMm: r.number(v['yMm'], `${path}.yMm`, { min: -PAGE_MAX_MM, max: PAGE_MAX_MM }),
    wMm: r.number(v['wMm'], `${path}.wMm`, { min: 0, max: PAGE_MAX_MM }),
    hMm: r.number(v['hMm'], `${path}.hMm`, { min: 0, max: PAGE_MAX_MM }),
    rotationDeg: r.number(v['rotationDeg'] ?? 0, `${path}.rotationDeg`, { min: -360, max: 360 }),
  };
  const text = (key: string) => readTextStyleRef(r, v[key], `${path}.${key}`);
  switch (v['kind']) {
    case 'text':
      return {
        ...base,
        kind: 'text',
        content: r.string(v['content'], `${path}.content`, PRINT_TEMPLATE_LIMITS.maxTextLength),
        style: text('style'),
        align: r.enumValue(v['align'] ?? 'left', H_ALIGNS, `${path}.align`, 'left'),
        valign: r.enumValue(v['valign'] ?? 'top', V_ALIGNS, `${path}.valign`, 'top'),
        autoShrink: r.optionalBoolean(v['autoShrink'], `${path}.autoShrink`) ?? false,
        paddingMm: r.number(v['paddingMm'] ?? 0, `${path}.paddingMm`, { min: 0, max: 100 }),
      };
    case 'image':
      return {
        ...base,
        kind: 'image',
        source: readImageSource(r, v['source'], `${path}.source`),
        fit: r.enumValue(v['fit'] ?? 'contain', IMAGE_FITS, `${path}.fit`, 'contain'),
        // Optional (centered when unset); unset keys come back from storage as null, and none go out as undefined.
        ...(v['align'] === undefined || v['align'] === null ? {} : { align: r.enumValue(v['align'], H_ALIGNS, `${path}.align`, 'center') }),
        ...(v['valign'] === undefined || v['valign'] === null ? {} : { valign: r.enumValue(v['valign'], V_ALIGNS, `${path}.valign`, 'middle') }),
      };
    case 'rect':
      return {
        ...base,
        kind: 'rect',
        stroke: readNullableStroke(r, v['stroke'], `${path}.stroke`),
        fill: readNullableColor(r, v['fill'], `${path}.fill`),
        cornerRadiusMm: r.number(v['cornerRadiusMm'] ?? 0, `${path}.cornerRadiusMm`, { min: 0, max: 1000 }),
      };
    case 'ellipse':
      return { ...base, kind: 'ellipse', stroke: readNullableStroke(r, v['stroke'], `${path}.stroke`), fill: readNullableColor(r, v['fill'], `${path}.fill`) };
    case 'polyline': {
      const points = r.array(v['pointsMm'], `${path}.pointsMm`, PRINT_TEMPLATE_LIMITS.maxPolylinePoints).map((p, i): [number, number] => {
        if (!Array.isArray(p) || p.length !== 2) {
          r.fail(`${path}.pointsMm[${i}]`, 'must be an [x, y] pair');
          return [0, 0];
        }
        return [r.number(p[0], `${path}.pointsMm[${i}][0]`), r.number(p[1], `${path}.pointsMm[${i}][1]`)];
      });
      if (points.length < 2) r.fail(`${path}.pointsMm`, 'must have at least 2 points');
      return {
        ...base,
        kind: 'polyline',
        pointsMm: points,
        closed: r.boolean(v['closed'] ?? false, `${path}.closed`),
        stroke: readNullableStroke(r, v['stroke'], `${path}.stroke`),
        fill: readNullableColor(r, v['fill'], `${path}.fill`),
      };
    }
    case 'viewport': {
      const allowedKinds = r.array(v['allowedKinds'], `${path}.allowedKinds`, VIEWPORT_KINDS.length).map((k, i) => r.enumValue(k, VIEWPORT_KINDS, `${path}.allowedKinds[${i}]`, 'PLAN_VECTOR'));
      const defaultKind = r.enumValue(v['defaultKind'], VIEWPORT_KINDS, `${path}.defaultKind`, 'PLAN_VECTOR');
      if (allowedKinds.length === 0) r.fail(`${path}.allowedKinds`, 'must allow at least one kind');
      if (new Set(allowedKinds).size !== allowedKinds.length) r.fail(`${path}.allowedKinds`, 'must not repeat a kind');
      if (allowedKinds.length > 0 && !allowedKinds.includes(defaultKind)) r.fail(`${path}.defaultKind`, 'must be one of the allowed kinds');
      return {
        ...base,
        kind: 'viewport',
        label: r.string(v['label'] ?? '', `${path}.label`),
        defaultKind,
        allowedKinds: allowedKinds as ViewportKind[],
        defaultScaleDen: r.number(v['defaultScaleDen'], `${path}.defaultScaleDen`, { min: 0.01, max: 1_000_000 }),
        frame: readNullableStroke(r, v['frame'], `${path}.frame`),
      };
    }
    case 'scaleBar':
      return {
        ...base,
        kind: 'scaleBar',
        viewportId: v['viewportId'] === null || v['viewportId'] === undefined ? null : r.id(v['viewportId'], `${path}.viewportId`),
        segments: r.integer(v['segments'] ?? 4, `${path}.segments`, { min: 1, max: 20 }),
        stroke: readStroke(r, v['stroke'], `${path}.stroke`),
        fill: readColor(r, v['fill'], `${path}.fill`),
        labelStyle: text('labelStyle'),
      };
    case 'northArrow':
      return {
        ...base,
        kind: 'northArrow',
        viewportId: v['viewportId'] === null || v['viewportId'] === undefined ? null : r.id(v['viewportId'], `${path}.viewportId`),
        variant: r.enumValue(v['variant'] ?? 'arrow', NORTH_ARROW_VARIANTS, `${path}.variant`, 'arrow'),
        stroke: readStroke(r, v['stroke'], `${path}.stroke`),
        fill: readColor(r, v['fill'], `${path}.fill`),
        labelStyle: text('labelStyle'),
      };
    case 'legend':
      return {
        ...base,
        kind: 'legend',
        viewportId: v['viewportId'] === null || v['viewportId'] === undefined ? null : r.id(v['viewportId'], `${path}.viewportId`),
        source: r.enumValue(v['source'], LEGEND_SOURCES, `${path}.source`, 'manual'),
        title: r.string(v['title'] ?? '', `${path}.title`),
        items: r.array(v['items'] ?? [], `${path}.items`, PRINT_TEMPLATE_LIMITS.maxLegendItems).map((item, i): LegendItem => {
          const it = r.record(item, `${path}.items[${i}]`);
          return { label: r.string(it['label'], `${path}.items[${i}].label`), color: readColor(r, it['color'], `${path}.items[${i}].color`) };
        }),
        style: text('style'),
      };
    case 'table':
      return readTable(r, v, path, base, text);
    case 'group': {
      if (depth >= PRINT_TEMPLATE_LIMITS.maxGroupDepth) r.fail(path, `groups nest at most ${PRINT_TEMPLATE_LIMITS.maxGroupDepth} deep`);
      const children = r
        .array(v['children'], `${path}.children`, PRINT_TEMPLATE_LIMITS.maxElements)
        .map((child, i) => (depth >= PRINT_TEMPLATE_LIMITS.maxGroupDepth ? null : readElement(r, child, `${path}.children[${i}]`, depth + 1)))
        .filter((child): child is PrintTemplateElement => child !== null);
      return { ...base, kind: 'group', children };
    }
    default:
      r.fail(`${path}.kind`, 'is not a known element kind');
      return null;
  }
}

function readImageSource(r: Reader, value: unknown, path: string): ImageSource {
  const v = r.record(value, path);
  if (v['kind'] === 'orgLogo') return { kind: 'orgLogo' };
  if (v['kind'] !== 'asset') r.fail(`${path}.kind`, 'must be asset or orgLogo');
  return { kind: 'asset', assetId: r.string(v['assetId'], `${path}.assetId`, 128) };
}

function readTable(
  r: Reader,
  v: UnknownRecord,
  path: string,
  base: Omit<Extract<PrintTemplateElement, { kind: 'table' }>, 'kind' | 'columns' | 'rows' | 'showHeader' | 'rowHeightMm' | 'growUp' | 'headerStyle' | 'cellStyle' | 'stroke'>,
  text: (key: string) => TextStyleRef,
): PrintTemplateElement {
  const columns = r.array(v['columns'], `${path}.columns`, PRINT_TEMPLATE_LIMITS.maxTableColumns).map((col, i): TableColumn => {
    const c = r.record(col, `${path}.columns[${i}]`);
    const cp = `${path}.columns[${i}]`;
    return {
      id: r.id(c['id'], `${cp}.id`),
      header: r.string(c['header'] ?? '', `${cp}.header`),
      widthMm: r.number(c['widthMm'], `${cp}.widthMm`, { exclusiveMin: 0, max: PAGE_MAX_MM }),
      cell: r.string(c['cell'] ?? '', `${cp}.cell`, PRINT_TEMPLATE_LIMITS.maxTextLength),
      align: r.enumValue(c['align'] ?? 'left', H_ALIGNS, `${cp}.align`, 'left'),
    };
  });
  if (columns.length === 0) r.fail(`${path}.columns`, 'must have at least one column');
  const rowsValue = r.record(v['rows'], `${path}.rows`);
  let rows: TableRows;
  if (rowsValue['kind'] === 'static') {
    const cells = r
      .array(rowsValue['cells'], `${path}.rows.cells`, PRINT_TEMPLATE_LIMITS.maxTableRows)
      .map((row, i) => r.array(row, `${path}.rows.cells[${i}]`, PRINT_TEMPLATE_LIMITS.maxTableColumns).map((cell, j) => r.string(cell, `${path}.rows.cells[${i}][${j}]`, PRINT_TEMPLATE_LIMITS.maxTextLength)));
    rows = { kind: 'static', cells };
  } else {
    if (rowsValue['kind'] !== 'revisions') r.fail(`${path}.rows.kind`, 'must be revisions or static');
    rows = {
      kind: 'revisions',
      maxRows: r.integer(rowsValue['maxRows'] ?? 5, `${path}.rows.maxRows`, { min: 1, max: PRINT_TEMPLATE_LIMITS.maxTableRows }),
      newestFirst: r.boolean(rowsValue['newestFirst'] ?? true, `${path}.rows.newestFirst`),
    };
  }
  return {
    ...base,
    kind: 'table',
    columns,
    rows,
    showHeader: r.boolean(v['showHeader'] ?? true, `${path}.showHeader`),
    rowHeightMm: r.number(v['rowHeightMm'], `${path}.rowHeightMm`, { exclusiveMin: 0, max: 200 }),
    growUp: r.boolean(v['growUp'] ?? false, `${path}.growUp`),
    headerStyle: text('headerStyle'),
    cellStyle: text('cellStyle'),
    stroke: readNullableStroke(r, v['stroke'], `${path}.stroke`),
  };
}

// ─── Template ───────────────────────────────────────────────────────────

function readField(r: Reader, value: unknown, path: string): PrintFieldDef {
  const v = r.record(value, path);
  const key = r.string(v['key'], `${path}.key`, 64);
  if (!PRINT_FIELD_KEY_PATTERN.test(key)) r.fail(`${path}.key`, 'must start with a letter and contain only letters, digits or "_"');
  const type = r.enumValue(v['type'] ?? 'text', FIELD_TYPES, `${path}.type`, 'text');
  const options = r.array(v['options'] ?? [], `${path}.options`, 100).map((o, i) => r.string(o, `${path}.options[${i}]`));
  if (type === 'select' && options.length === 0) r.fail(`${path}.options`, 'a select field needs at least one option');
  return { key, label: r.string(v['label'], `${path}.label`), type, options, defaultValue: r.string(v['defaultValue'] ?? '', `${path}.defaultValue`, PRINT_TEMPLATE_LIMITS.maxTextLength) };
}

function readPage(r: Reader, value: unknown, path: string): PrintPageSpec {
  const v = r.record(value, path);
  return {
    id: r.id(v['id'], `${path}.id`),
    name: r.string(v['name'] ?? '', `${path}.name`),
    paper: readPaper(r, v['paper'], `${path}.paper`),
    orientation: r.enumValue(v['orientation'], ORIENTATIONS, `${path}.orientation`, 'landscape'),
    marginsMm: readMargins(r, v['marginsMm'], `${path}.marginsMm`),
    repeat: r.enumValue(v['repeat'] ?? 'none', PAGE_REPEATS, `${path}.repeat`, 'none'),
    elements: r
      .array(v['elements'], `${path}.elements`, PRINT_TEMPLATE_LIMITS.maxElements)
      .map((el, i) => readElement(r, el, `${path}.elements[${i}]`, 0))
      .filter((el): el is PrintTemplateElement => el !== null),
  };
}

/** Cross-references: unique ids/keys, style and viewport links, limits. */
function checkTemplateReferences(r: Reader, spec: PrintTemplateSpec): void {
  const styleIds = new Set<string>();
  spec.styles.forEach((style, i) => {
    if (styleIds.has(style.id)) r.fail(`styles[${i}].id`, `duplicate style id "${style.id}"`);
    styleIds.add(style.id);
  });
  const fieldKeys = new Set<string>();
  spec.fields.forEach((field, i) => {
    if (fieldKeys.has(field.key)) r.fail(`fields[${i}].key`, `duplicate field key "${field.key}"`);
    fieldKeys.add(field.key);
  });

  const pageIds = new Set<string>();
  const elementIds = new Set<string>();
  let elementCount = 0;
  spec.pages.forEach((page, p) => {
    const pagePath = `pages[${p}]`;
    if (pageIds.has(page.id)) r.fail(`${pagePath}.id`, `duplicate page id "${page.id}"`);
    pageIds.add(page.id);

    const viewportIds = new Set<string>();
    forEachElement(page.elements, (element) => {
      if (element.kind === 'viewport') viewportIds.add(element.id);
    });
    forEachElement(page.elements, (element) => {
      elementCount += 1;
      const at = `${pagePath} element "${element.id}"`;
      if (elementIds.has(element.id)) r.fail(at, `duplicate element id "${element.id}"`);
      elementIds.add(element.id);
      const linked = element.kind === 'scaleBar' || element.kind === 'northArrow' || element.kind === 'legend' ? element.viewportId : null;
      if (linked !== null && !viewportIds.has(linked)) r.fail(at, `links viewport "${linked}", which is not on this page`);
      for (const ref of styleRefsOf(element)) {
        if (ref.styleId !== undefined && !styleIds.has(ref.styleId)) r.fail(at, `uses unknown style "${ref.styleId}"`);
      }
    });
  });
  if (elementCount > PRINT_TEMPLATE_LIMITS.maxElements) r.fail('pages', `a template may hold at most ${PRINT_TEMPLATE_LIMITS.maxElements} elements`);
}

function styleRefsOf(element: PrintTemplateElement): TextStyleRef[] {
  switch (element.kind) {
    case 'text':
    case 'legend':
      return [element.style];
    case 'scaleBar':
    case 'northArrow':
      return [element.labelStyle];
    case 'table':
      return [element.headerStyle, element.cellStyle];
    default:
      return [];
  }
}

export function parsePrintTemplateSpec(input: unknown): PrintTemplateParseResult {
  const r = new Reader();
  const v = r.record(input, '');
  if (v['schemaVersion'] !== PRINT_TEMPLATE_SCHEMA_VERSION) r.fail('schemaVersion', `must be ${PRINT_TEMPLATE_SCHEMA_VERSION}`);
  const defaults = r.record(v['defaults'], 'defaults');
  const spec: PrintTemplateSpec = {
    schemaVersion: PRINT_TEMPLATE_SCHEMA_VERSION,
    defaults: { textStyle: readTextStyle(r, defaults['textStyle'], 'defaults.textStyle'), stroke: readStroke(r, defaults['stroke'], 'defaults.stroke') },
    styles: r.array(v['styles'] ?? [], 'styles', PRINT_TEMPLATE_LIMITS.maxStyles).map((s, i): NamedTextStyle => {
      const sv = r.record(s, `styles[${i}]`);
      return { id: r.id(sv['id'], `styles[${i}].id`), name: r.string(sv['name'], `styles[${i}].name`), style: readTextStyleFields(r, r.record(sv['style'], `styles[${i}].style`), `styles[${i}].style`, false) };
    }),
    fields: r.array(v['fields'] ?? [], 'fields', PRINT_TEMPLATE_LIMITS.maxFields).map((f, i) => readField(r, f, `fields[${i}]`)),
    pages: r.array(v['pages'], 'pages', PRINT_TEMPLATE_LIMITS.maxPages).map((p, i) => readPage(r, p, `pages[${i}]`)),
  };
  if (spec.pages.length === 0) r.fail('pages', 'a template needs at least one page');
  checkTemplateReferences(r, spec);
  return r.issues.length > 0 ? { ok: false, issues: r.issues } : { ok: true, spec };
}

/** Throws with the issue list — for code paths that only handle known-good data (builtins, tests). */
export function validatePrintTemplateSpec(input: unknown): PrintTemplateSpec {
  const result = parsePrintTemplateSpec(input);
  // `in` narrowing: libs compiling this file without strictNullChecks can't narrow on `ok`.
  if ('issues' in result) throw new Error(`Invalid print template: ${result.issues.map((i) => `${i.path}: ${i.message}`).join('; ')}`);
  return result.spec;
}

// ─── Sheet ──────────────────────────────────────────────────────────────

function readTemplateRef(r: Reader, value: unknown, path: string): PrintTemplateRef {
  const v = r.record(value, path);
  return {
    source: r.enumValue(v['source'], ['builtin', 'org'] as const, `${path}.source`, 'builtin'),
    id: r.string(v['id'], `${path}.id`, 128),
    version: r.integer(v['version'], `${path}.version`, { min: 0 }),
  };
}

function readHeatmap(r: Reader, value: unknown, path: string): HeatmapViewportSettings | null {
  if (value === null || value === undefined) return null;
  const v = r.record(value, path);
  const min = r.number(v['min'], `${path}.min`);
  const max = r.number(v['max'], `${path}.max`);
  if (max <= min) r.fail(`${path}.max`, 'must be greater than min');
  return {
    attribute: r.string(v['attribute'], `${path}.attribute`, 64),
    sourceId: r.nullableString(v['sourceId'] ?? null, `${path}.sourceId`, 128),
    gradient: r.enumValue(v['gradient'], GRADIENT_TYPES, `${path}.gradient`, 'SPECTRAL'),
    min,
    max,
  };
}

function readXray(r: Reader, value: unknown, path: string): XrayViewportSettings | null {
  if (value === null || value === undefined) return null;
  const v = r.record(value, path);
  return {
    alpha: r.number(v['alpha'], `${path}.alpha`, { exclusiveMin: 0, max: 1 }),
    referenceMetersPerPixel: r.number(v['referenceMetersPerPixel'], `${path}.referenceMetersPerPixel`, { exclusiveMin: 0 }),
    referencePointsPerM2: r.nullableNumber(v['referencePointsPerM2'] ?? null, `${path}.referencePointsPerM2`),
    zMin: r.nullableNumber(v['zMin'], `${path}.zMin`),
    zMax: r.nullableNumber(v['zMax'], `${path}.zMax`),
  };
}

function readPlanLayers(r: Reader, value: unknown, path: string): Partial<Record<PlanLayerCategory, PlanLayerPen>> {
  const layersValue = r.record(value ?? {}, path);
  const layers: Partial<Record<PlanLayerCategory, PlanLayerPen>> = {};
  for (const [category, pen] of Object.entries(layersValue)) {
    const lp = `${path}.${category}`;
    if (!oneOf(category, PLAN_LAYER_CATEGORIES)) {
      r.fail(lp, 'is not a plan layer category');
      continue;
    }
    const pv = r.record(pen, lp);
    layers[category] = {
      visible: r.boolean(pv['visible'], `${lp}.visible`),
      weightMm: r.number(pv['weightMm'], `${lp}.weightMm`, { min: 0, max: PRINT_TEMPLATE_LIMITS.maxLineWeightMm }),
      color: readNullableColor(r, pv['color'], `${lp}.color`),
    };
  }
  return layers;
}

function readPlanTextSizes(r: Reader, value: unknown, path: string): Partial<Record<PlanTextCategory, TextSize>> {
  const sizesValue = r.record(value ?? {}, path);
  const textSizes: Partial<Record<PlanTextCategory, TextSize>> = {};
  for (const [category, size] of Object.entries(sizesValue)) {
    if (!oneOf(category, PLAN_TEXT_CATEGORIES)) {
      r.fail(`${path}.${category}`, 'is not a plan text category');
      continue;
    }
    textSizes[category] = readTextSize(r, size, `${path}.${category}`);
  }
  return textSizes;
}

function readViewportSettings(r: Reader, value: unknown, path: string): ViewportSettings {
  const v = r.record(value, path);
  const center = r.record(v['center'], `${path}.center`);
  const layers = readPlanLayers(r, v['layers'], `${path}.layers`);
  const textSizes = readPlanTextSizes(r, v['textSizes'], `${path}.textSizes`);
  const floorId = r.nullableString(v['floorId'], `${path}.floorId`, 128);
  if (floorId !== null && !FLOOR_ID_PATTERN.test(floorId)) r.fail(`${path}.floorId`, 'is not a valid floor id');
  const capturedAt = r.nullableString(v['capturedAt'], `${path}.capturedAt`, 64);
  if (capturedAt !== null && Number.isNaN(Date.parse(capturedAt))) r.fail(`${path}.capturedAt`, 'must be an ISO timestamp');
  return {
    kind: r.enumValue(v['kind'], VIEWPORT_KINDS, `${path}.kind`, 'PLAN_VECTOR'),
    floorId,
    followPageFloor: r.boolean(v['followPageFloor'] ?? true, `${path}.followPageFloor`),
    center: { x: r.number(center['x'], `${path}.center.x`), y: r.number(center['y'], `${path}.center.y`) },
    headingDeg: r.number(v['headingDeg'] ?? 0, `${path}.headingDeg`, { min: -360, max: 360 }),
    scaleDen: r.number(v['scaleDen'], `${path}.scaleDen`, { min: 0.01, max: 1_000_000 }),
    dpi: r.number(v['dpi'], `${path}.dpi`, { min: 36, max: 1200 }),
    layers,
    textSizes,
    monochrome: r.boolean(v['monochrome'] ?? true, `${path}.monochrome`),
    planOverlay: r.boolean(v['planOverlay'] ?? false, `${path}.planOverlay`),
    heatmap: readHeatmap(r, v['heatmap'], `${path}.heatmap`),
    xray: readXray(r, v['xray'], `${path}.xray`),
    screenMetersPerPixel: r.nullableNumber(v['screenMetersPerPixel'] ?? null, `${path}.screenMetersPerPixel`),
    viewMode: r.nullableString(v['viewMode'], `${path}.viewMode`, 64),
    capturedAt,
  };
}

function readSheetPageElements(r: Reader, value: unknown, path: string): Record<string, PrintTemplateElement[]> {
  const v = r.record(value ?? {}, path);
  const out: Record<string, PrintTemplateElement[]> = {};
  for (const [pageId, list] of Object.entries(v).slice(0, PRINT_TEMPLATE_LIMITS.maxPages)) {
    const elements = r.array(list, `${path}.${pageId}`, 200).flatMap((item, i) => {
      const element = readElement(r, item, `${path}.${pageId}[${i}]`, 0);
      if (element && !oneOf(element.kind, SHEET_EDITABLE_KINDS)) {
        r.fail(`${path}.${pageId}[${i}]`, 'a sheet can only add views, scale bars, north arrows and legends');
        return [];
      }
      return element ? [element] : [];
    });
    out[pageId] = elements;
  }
  return out;
}

function readSheetLegendLabels(r: Reader, value: unknown, path: string): Record<string, Record<string, string>> {
  const v = r.record(value ?? {}, path);
  const out: Record<string, Record<string, string>> = {};
  for (const [elementId, labels] of Object.entries(v).slice(0, 500)) {
    const row = r.record(labels, `${path}.${elementId}`);
    out[elementId] = {};
    for (const [key, text] of Object.entries(row).slice(0, PRINT_TEMPLATE_LIMITS.maxLegendItems * 2)) {
      if (text === null || text === undefined) continue;
      out[elementId][key] = r.string(text, `${path}.${elementId}.${key}`);
    }
  }
  return out;
}

function readSheetDimensions(r: Reader, value: unknown, path: string): Record<string, SheetDimension[]> {
  const v = r.record(value ?? {}, path);
  const out: Record<string, SheetDimension[]> = {};
  const coordinate = (value: unknown, at: string) => r.number(value, at, { min: -10_000_000, max: 10_000_000 });
  const point = (value: unknown, at: string) => {
    const p = r.record(value, at);
    return { x: coordinate(p['x'], `${at}.x`), y: coordinate(p['y'], `${at}.y`) };
  };
  for (const [key, list] of Object.entries(v).slice(0, 2000)) {
    if (!VIEWPORT_KEY_PATTERN.test(key)) {
      r.fail(`${path}.${key}`, 'is not a valid viewport key');
      continue;
    }
    out[key] = r.array(list, `${path}.${key}`, 500).map((item, i) => {
      const at = `${path}.${key}[${i}]`;
      const d = r.record(item, at);
      const dimension: SheetDimension = { id: r.id(d['id'], `${at}.id`), a: point(d['a'], `${at}.a`), b: point(d['b'], `${at}.b`), offsetM: r.number(d['offsetM'] ?? 0, `${at}.offsetM`, { min: -100_000, max: 100_000 }) };
      // Style: only what is set (unset keys come back from storage as null).
      if (d['ends'] !== undefined && d['ends'] !== null) dimension.ends = r.enumValue(d['ends'], SHEET_DIMENSION_ENDS, `${at}.ends`, 'tick');
      if (d['lineStyle'] !== undefined && d['lineStyle'] !== null) dimension.lineStyle = r.enumValue(d['lineStyle'], SHEET_DIMENSION_LINE_STYLES, `${at}.lineStyle`, 'solid');
      if (d['extensionLines'] !== undefined && d['extensionLines'] !== null) dimension.extensionLines = r.enumValue(d['extensionLines'], SHEET_DIMENSION_EXTENSION_STYLES, `${at}.extensionLines`, 'dashed');
      if (d['textScale'] !== undefined && d['textScale'] !== null) dimension.textScale = r.number(d['textScale'], `${at}.textScale`, { min: 0.25, max: 4 });
      return dimension;
    });
  }
  return out;
}

function readSheetElementEdits(r: Reader, value: unknown, path: string): Record<string, SheetElementEdit> {
  const v = r.record(value ?? {}, path);
  const out: Record<string, SheetElementEdit> = {};
  for (const [id, stored] of Object.entries(v).slice(0, 2000)) {
    const e = r.record(stored, `${path}.${id}`);
    // Unset sides come back from storage as null: only numbers are kept, and no undefined keys go out.
    const edit: SheetElementEdit = {};
    for (const key of ['xMm', 'yMm', 'wMm', 'hMm'] as const) {
      if (e[key] !== undefined && e[key] !== null) edit[key] = r.number(e[key], `${path}.${id}.${key}`, { min: -10_000, max: 10_000 });
    }
    if (typeof e['outline'] === 'boolean') edit.outline = e['outline'];
    if (e['removed'] === true) edit.removed = true;
    out[id] = edit;
  }
  return out;
}

export function parseSheetDoc(input: unknown): SheetDocParseResult {
  const r = new Reader();
  const v = r.record(input, '');
  if (v['schemaVersion'] !== SHEET_DOC_SCHEMA_VERSION) r.fail('schemaVersion', `must be ${SHEET_DOC_SCHEMA_VERSION}`);

  const fieldValuesInput = r.record(v['fieldValues'] ?? {}, 'fieldValues');
  const fieldValues: Record<string, string> = {};
  for (const [key, value] of Object.entries(fieldValuesInput).slice(0, PRINT_TEMPLATE_LIMITS.maxFields)) {
    if (!PRINT_FIELD_KEY_PATTERN.test(key)) r.fail(`fieldValues.${key}`, 'is not a valid field key');
    else fieldValues[key] = r.string(value, `fieldValues.${key}`, PRINT_TEMPLATE_LIMITS.maxTextLength);
  }

  const viewportsInput = r.record(v['viewports'] ?? {}, 'viewports');
  const viewports: Record<string, ViewportSettings> = {};
  const viewportEntries = Object.entries(viewportsInput);
  if (viewportEntries.length > 2000) r.fail('viewports', 'too many viewport entries');
  for (const [key, value] of viewportEntries.slice(0, 2000)) {
    if (!VIEWPORT_KEY_PATTERN.test(key)) r.fail(`viewports.${key}`, 'is not a valid viewport key');
    else viewports[key] = readViewportSettings(r, value, `viewports.${key}`);
  }

  const floorIds =
    v['floorIds'] === null || v['floorIds'] === undefined
      ? null
      : r.array(v['floorIds'], 'floorIds', 500).map((id, i) => {
          const floorId = r.string(id, `floorIds[${i}]`, 128);
          if (!FLOOR_ID_PATTERN.test(floorId)) r.fail(`floorIds[${i}]`, 'is not a valid floor id');
          return floorId;
        });

  const doc: SheetDoc = {
    schemaVersion: SHEET_DOC_SCHEMA_VERSION,
    templateRef: readTemplateRef(r, v['templateRef'], 'templateRef'),
    fieldValues,
    revisions: r.array(v['revisions'] ?? [], 'revisions', PRINT_TEMPLATE_LIMITS.maxTableRows).map((rev, i): SheetRevision => {
      const rv = r.record(rev, `revisions[${i}]`);
      return {
        id: r.id(rv['id'], `revisions[${i}].id`),
        rev: r.string(rv['rev'], `revisions[${i}].rev`, 16),
        date: r.string(rv['date'] ?? '', `revisions[${i}].date`, 32),
        description: r.string(rv['description'] ?? '', `revisions[${i}].description`, 500),
        by: r.string(rv['by'] ?? '', `revisions[${i}].by`, 64),
      };
    }),
    floorIds,
    viewports,
    hiddenPages: r.array(v['hiddenPages'] ?? [], 'hiddenPages', 2000).map((key, i) => r.string(key, `hiddenPages[${i}]`, 200)),
    pageElements: readSheetPageElements(r, v['pageElements'], 'pageElements'),
    elementEdits: readSheetElementEdits(r, v['elementEdits'], 'elementEdits'),
    dimensions: readSheetDimensions(r, v['dimensions'], 'dimensions'),
    legendLabels: readSheetLegendLabels(r, v['legendLabels'], 'legendLabels'),
  };
  return r.issues.length > 0 ? { ok: false, issues: r.issues } : { ok: true, doc };
}
